import { FC } from 'react';
import classNames from 'classnames';
import { useMutation } from '@apollo/client';
import { UpdateClientPinnedDocument } from 'wb-reddit/graphql/generated';
import {
	SolidArrowUp, SolidArrowDown, RowHeightVariantsEnum, DashboardCellRendererFrame, useKeyboardAction,
} from '@aleph/shared-components';
import { ICellRendererParams } from 'ag-grid-community';
import { captureException } from '@sentry/react';

const performanceArrows = {
	bestPerformers: {
		arrows: [
			<SolidArrowUp className="fill-green opacity-1" />,
			<SolidArrowUp className="fill-green opacity-60" />,
			<SolidArrowUp className="fill-green opacity-20" />,
		],
	},
	worstPerformers: {
		arrows: [
			<SolidArrowDown className="fill-red opacity-20" />,
			<SolidArrowDown className="fill-red opacity-60" />,
			<SolidArrowDown className="fill-red opacity-1" />,
		],
	},
};

const ClientSettingsCellRenderer: FC<ICellRendererParams> = (params) => {
	const [setClientPinned] = useMutation(UpdateClientPinnedDocument);

	const onSetClientPinned = async () => {
		const pinned = !params.data?.isDashboardView;

		try {
			let allCollapsed = true;

			params.api.forEachNode(node => {
				if (node.expanded) {
					allCollapsed = false;
				}
			});

			params.node.setData({
				...params.data,
				isDashboardView:          pinned,
				clientPinned:             pinned,
				supergridIsDashboardView: pinned,
			});

			if (allCollapsed) {
				params.node.setRowHeight(RowHeightVariantsEnum[pinned ? 'dashboardViewRowHeight' : 'mediumRowHeight']);
				params.api.onRowHeightChanged();
			}

			await setClientPinned({
				variables: {
					clientId:     params.data?.id,
					clientPinned: pinned,
				},
			});
		} catch (e) {
			captureException(e);
		}
	};

	useKeyboardAction({
		element: params.eGridCell,
		handler: onSetClientPinned,
	});

	return (
		<DashboardCellRendererFrame>
			<div
				onClick={() => onSetClientPinned()}
				className={
					classNames(
						'w-full cursor-pointer flex justify-center h-full items-center',
					)
				}
			>
				<div
					className={classNames('rounded-full w-[24px] h-[14px] flex relative items-center px-[1px]', {
						'bg-gray-300': !params.data.isDashboardView,
						'bg-blue':     params.data.isDashboardView,
					})}
				>
					<div
						className={classNames(
							'bg-white transition-all right-[11px]',
							'rounded-full absolute h-[12px] w-[12px]',
							{
								'!right-[1px]': params.data.isDashboardView,
							},
						)}
					/>
				</div>
			</div>
			<div className="h-full" />
			<div className="h-full">
				{performanceArrows.bestPerformers.arrows.map((arrow, idx) => (
					<div key={idx}
						className={classNames(
							'flex items-center justify-center',
							'h-[32px] border-dashed border-b-[1px]',
						)}
					>
						{arrow}
					</div>
				))}
			</div>
			<div className="h-full">
				{performanceArrows.worstPerformers.arrows.map((arrow, idx) => (
					<div key={idx}
						className={classNames(
							'flex items-center justify-center',
							'h-[32px] border-dashed border-b-[1px]',
						)}
					>
						{arrow}
					</div>
				))}
			</div>
		</DashboardCellRendererFrame>
	);
};

export default ClientSettingsCellRenderer;
